import {
  AppPageHeader,
  ColumnsType,
  InfiniteTable,
} from '@/components/_common';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import LoadMoreSummary from '@/components/_common/LoadMoreSummary';
import ResultOrderFilterFormItem from '@/components/_common/ResultOrderFilterFormItem';
import StatusTag from '@/components/_common/StatusTag';
import { useStockItems } from '@/core/domain/stock/StockItems/StockItems.hooks';
import {
  FilterTypeEnum,
  ListStockItemsQuery,
  StockItem,
  StockItemType,
} from '@/core/domain/stock/StockItems/StockItems.types';
import { useDebounce } from '@/hooks';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { FieldData } from '@/types/FieldData';
import { quantityFormatter } from '@/utils/number';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import { getTableScrollX } from '@/utils/table';
import { Col, Form, Radio, Row, Select, Typography } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';

const { Title, Text } = Typography;

interface Props {
  open?: boolean;
  companyId?: number;
  selectedItems?: StockItem[];
  selectedStockItems: StockItem[];
  setSelectedStockItems: (value: StockItem[]) => void;
}

export default function StockTransferSelectItemsList({
  open = true,
  companyId,
  selectedItems,
  selectedStockItems,
  setSelectedStockItems,
}: Props) {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<ListStockItemsQuery>({
    active: true,
    itemType: StockItemType.Part,
    companiesIds: !!companyId ? [companyId] : [],
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, hasNextPage, fetchNextPage } = useStockItems(
    open && !!companyId,
    queryDebounce,
  );
  const stockItems = flatInfiniteResult(data);

  const columns: ColumnsType<StockItem> = [
    useConfigurationColumnsPart({ isSku: true }),
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.itemName',
      }),
      width: 250,
      render: (value) => <EllipsisHoverToggle>{value}</EllipsisHoverToggle>,
    },
    {
      ellipsis: true,
      width: 120,
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.type',
      }),
      render(row: StockItem) {
        let label = '-';

        if (row.partId) {
          label = intl.formatMessage({
            id: 'stockCorrections.domain.items.type.item_type.part',
          });
        } else if (row.consumableId) {
          label = intl.formatMessage({
            id: 'stockCorrections.domain.items.type.item_type.consumable',
          });
        }

        return <StatusTag label={label} color="blue" />;
      },
    },
    {
      ellipsis: true,
      width: 120,
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.stock',
      }),
      align: 'right',
      render: (_: unknown, record: StockItem) => {
        if (!companyId) return '-';

        const companyStock = record.stock?.find(
          (a) => companyId === a.company.id,
        );

        if (!companyStock) return '-';

        return quantityFormatter(companyStock?.stock);
      },
    },
    {
      ellipsis: true,
      width: 140,
      dataIndex: 'availableStock',
      title: intl.formatMessage({
        id: 'stockCorrections.domain.items.availableStock',
      }),
      align: 'right',
      render: (_: unknown, record: StockItem) => {
        if (!companyId) return '-';

        const companyStock = record.stock?.find(
          (stockItem) => companyId === stockItem.company.id,
        );

        if (!companyStock) return '-';

        return quantityFormatter(companyStock?.availableStock);
      },
    },
  ];

  const handleFiltersChange = (_: FieldData[], allFields: FieldData[]) => {
    allFields.forEach((field) => {
      const name = field.name as string[];
      const { value } = field;

      if (value) {
        setQueryParams((prev) => ({
          ...prev,
          [name[0]]: value,
        }));
      }
    });
  };

  return (
    <Row gutter={16}>
      <Col flex="1">
        <div style={{ width: '100%' }}>
          <div style={{ marginBottom: '24px' }}>
            <Title level={4}>Transferência de estoque</Title>

            <Text>
              Filtre e selecione os itens a serem adicionados na para realizar a
              transferência de estoque.
            </Text>
          </div>

          <AppPageHeader
            onSearch={(value: string) =>
              setQueryParams((prev) => ({ ...prev, search: value }))
            }
            extraFilters={
              <Form onFieldsChange={handleFiltersChange}>
                <Row gutter={[16, 16]}>
                  <Col span={24} md={8}>
                    <Form.Item
                      noStyle
                      name="filterType"
                      initialValue={FilterTypeEnum.free}
                    >
                      <Select
                        style={{ width: '100%' }}
                        options={[
                          {
                            label: 'Por campo - Livre',
                            value: FilterTypeEnum.free,
                          },
                          {
                            label: 'Por campo - Especifico',
                            value: FilterTypeEnum.specific,
                          },
                        ]}
                        onChange={(value) =>
                          setQueryParams((prev) => ({
                            ...prev,
                            filterType: value,
                          }))
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24} md={8}>
                    <ResultOrderFilterFormItem />
                  </Col>
                </Row>
              </Form>
            }
          />

          <Col span={24}>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              size="large"
              value={queryParams?.itemType}
              onChange={({ target }) => {
                setQueryParams((prev) => ({ ...prev, itemType: target.value }));

                return target.value;
              }}
            >
              <Radio value={StockItemType.Part}>Peça</Radio>

              <Radio value={StockItemType.Consumable}>Insumo</Radio>
            </Radio.Group>
          </Col>

          <InfiniteTable<StockItem>
            columns={columns}
            dataSource={stockItems}
            loading={isFetching}
            className="scroll-table"
            rowKey={(row) => row.partId ?? row.consumableId}
            pagination={false}
            scroll={{ x: getTableScrollX(columns) }}
            summary={() =>
              hasNextPage ? (
                <LoadMoreSummary
                  onClick={fetchNextPage}
                  columnsCount={columns.length + 1}
                />
              ) : null
            }
            rowSelection={{
              preserveSelectedRowKeys: true,
              onChange: (_, items) => {
                const getId = (item: StockItem) =>
                  item.partId ?? item.consumableId;

                const selectedIds = new Set(items.map(getId));

                const combinedItems = [
                  ...items,
                  ...selectedStockItems.filter((item) =>
                    selectedIds.has(getId(item)),
                  ),
                ];

                const uniqueItems = combinedItems.reduce(
                  (acc: StockItem[], item) => {
                    const itemId = getId(item);
                    const exists = acc.some(
                      (accItem) => getId(accItem) === itemId,
                    );
                    if (!exists) acc.push(item);
                    return acc;
                  },
                  [],
                );

                setSelectedStockItems(uniqueItems);
              },
              selectedRowKeys: selectedStockItems.map(
                (item) => item.partId ?? item.consumableId,
              ),
              getCheckboxProps: (item) => {
                const getId = (i: StockItem) => i.partId ?? i.consumableId;
                const itemId = getId(item);

                const isAlreadySelected = selectedItems?.some(
                  (selectedItem) => getId(selectedItem) === itemId,
                );

                const companyStock = item.stock?.find(
                  (a) => companyId === a.company.id,
                );

                return {
                  disabled:
                    isAlreadySelected || companyStock?.availableStock === 0,
                };
              },
            }}
          />
        </div>
      </Col>
    </Row>
  );
}
